/** ISO/IEC 7813 Track 1 cardholder name (max 26). The `#dbg` tail is extra. */
export const CARDHOLDER_NAME_MAX_LENGTH = 26;

/**
 * HTML maxLength for the cardholder field.
 * Track 1 stays 26 characters; the rest is room for a CVS `#dbg` test code
 * (`#`, `=`, keys). Do not bind the input to 26 or those codes cannot be typed.
 */
export const CARDHOLDER_NAME_INPUT_MAX_LENGTH = 128;

const DBG_SENTINEL = "#dbg";

/** Track 1 name, optional `#dbg …` tail. Same shape as the backend pattern. */
const CARDHOLDER_NAME_PATTERN =
  /^(?=.*[A-Z])[A-Z .'-]{1,26}(?: #dbg.*)?$/;

function formatTrack1(fragment: string): string {
  return fragment
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z .'-]/g, "")
    .replace(/ {2,}/g, " ")
    .slice(0, CARDHOLDER_NAME_MAX_LENGTH);
}

function formatDebugTail(suffix: string): string {
  const body = suffix.slice(DBG_SENTINEL.length);
  const cleaned = body
    .toUpperCase()
    .replace(/[^A-Z0-9= _.-]/g, "")
    .replace(/ {2,}/g, " ");
  return `${DBG_SENTINEL}${cleaned}`;
}

/**
 * Everything after the first `#` is a debug draft, even before `#dbg` is
 * complete. A space or `=` mid-keystroke must not drop the tail back into
 * the 26-character track-1 filter.
 */
function formatDebugDraft(fromHash: string): string {
  if (fromHash.toLowerCase().startsWith(DBG_SENTINEL)) {
    return formatDebugTail(fromHash);
  }
  return fromHash
    .toLowerCase()
    .replace(/[^a-z0-9=# _.-]/g, "")
    .replace(/ {2,}/g, " ");
}

/** Fold accents, uppercase the name, keep a `#dbg` test code (`#` and `=`). */
export function formatCardholderNameInput(raw: string): string {
  const hash = raw.indexOf("#");
  if (hash === -1) return formatTrack1(raw);

  const head = formatTrack1(raw.slice(0, hash)).trimEnd();
  const tail = formatDebugDraft(raw.slice(hash));
  if (!tail) return head;
  const full = head ? `${head} ${tail}` : tail;
  return full.slice(0, CARDHOLDER_NAME_INPUT_MAX_LENGTH);
}

export function isValidCardholderName(value: string): boolean {
  return CARDHOLDER_NAME_PATTERN.test(value.trim());
}
